import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Swords, Trophy, Users } from "lucide-react";
import api from "@/lib/api";
import { ASSETS } from "@/lib/assets";
import Countdown from "@/components/Countdown";

const STATUS = {
  active: { l: "ACCEPTING ENTRIES", c: "bg-neon text-black" },
  voting: { l: "VOTING OPEN", c: "bg-gold text-black" },
  upcoming: { l: "UPCOMING", c: "bg-ink3 text-cream" },
  ended: { l: "ENDED", c: "bg-danger text-white" },
};

export default function Battles() {
  const { data: battles = [], isLoading } = useQuery({
    queryKey: ["battles"], queryFn: async () => (await api.get("/battles")).data,
  });

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
      <span className="tag">Weekly Showdowns</span>
      <h1 className="headline text-cream text-4xl sm:text-6xl mt-5">MEME <span className="text-neon">BATTLES</span></h1>
      <p className="font-mono text-dim mt-4 max-w-xl">Submit your meme. The crew votes. One gets crowned.</p>
      <p className="font-mono text-dim/80 mt-2 max-w-xl text-sm">Earn ROBBED REP, collect badges, climb the leaderboard, and build your reputation. No financial rewards are promised — for now, it's all about the memes, the crown, and the bragging rights.</p>
      <div className="flex flex-wrap gap-3 mt-5">
        <Link to="/hall-of-fame" className="btn-ghost !py-2 !px-4 !text-xs inline-flex !border-gold hover:!bg-gold" data-testid="hof-link">🏆 Hall Of Fame</Link>
        <Link to="/leaderboard" className="btn-ghost !py-2 !px-4 !text-xs inline-flex" data-testid="lb-link">📊 Leaderboard</Link>
      </div>

      {isLoading ? (
        <p className="font-mono text-dim mt-10">Loading battles...</p>
      ) : battles.length === 0 ? (
        <div className="flex flex-col items-center py-20 text-center">
          <img src={ASSETS.shrugging} alt="empty" className="w-40" />
          <p className="font-display text-cream mt-4 uppercase">No battles yet. Check back soon.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
          {battles.map((b, i) => {
            const st = STATUS[b.status] || STATUS.upcoming;
            return (
              <motion.div key={b.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}>
                <Link to={`/battles/${b.id}`} className="comic-card p-6 block hover:border-neon transition-colors" data-testid={`battle-${b.id}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-[10px] text-dim">BATTLE #{String(b.number || 1).padStart(3, "0")}</span>
                    <span className={`tag !text-[9px] !px-2 !py-0.5 ${st.c}`}>{st.l}</span>
                  </div>
                  <Swords className="w-8 h-8 text-neon mt-4" />
                  <h3 className="font-display font-900 text-cream text-xl uppercase mt-3">{b.title}</h3>
                  {b.prompt && <p className="font-mono text-xs text-dim mt-2">{b.prompt}</p>}
                  <div className="flex items-center gap-4 mt-4 font-mono text-xs text-cream/80">
                    <span className="flex items-center gap-1"><Users className="w-4 h-4 text-neon" /> {b.submissionCount || 0}</span>
                    {["active", "voting"].includes(b.status) && b.endsAt && <Countdown endsAt={b.endsAt} />}
                    {b.status === "ended" && b.winnerSubmissionId && <span className="flex items-center gap-1 text-gold"><Trophy className="w-4 h-4" /> Crowned</span>}
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
}
